/* eslint-disable prettier/prettier */
/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import useStateRef from "react-usestateref";

import sleep from "utils/sleep";

import useComponentDidMount from "./useComponentDidMount";
import { tryCatcher } from "./useTryCatch";

import { FetchError, FetchSuccess } from "lib";

const useFetcher = (fetcher, opts) => {
  const settings = {
    defaultState: null,
    startOnMount: true,
    startAfter: 0,
    key: null, // key is important for caching, no key means no caching.
    hasCache: true,
    shouldResetData: () => false,
    onSuccess: () => { },
    onError: () => { },
    onDataChange: () => { },
    triggerLoadingOnMutate: true,
    shouldRefetchOnKeyChange: true,
    shapeData: (data) => data,
    enabled: true, // if false, the fetcher will not be called.
    retries: 0,
    retryDelay: 1000,
    refetchOnConditions: [],
    shouldShapeDefaultState: true,
    resetStateOnError: false,
  };

  if (opts) {
    Object.assign(settings, opts);

    if (opts.defaultState && settings.shouldShapeDefaultState) {
      const onShape = settings.shapeData;
      settings.defaultState = onShape(opts.defaultState);
    }

    settings.startOnMount = settings.enabled ? settings.startOnMount : false; // if enabled is false, startOnMount should be false.
  }

  const { startAfter, key, onSuccess, onError } = settings;
  const [cache, setCache] = useState({});

  const mutateFn = async (payload, newKey) => {
    const _key = newKey || key;
    if (settings.triggerLoadingOnMutate) {
      setState((prev) => ({ ...prev, isLoading: true }));
      await sleep(1000);
    }

    let data = payload;
    if (typeof payload === "function") {
      data = payload(stateRef.current.data);
    }

    if (_key) {
      setCache((prev) => ({ ...prev, [_key]: data }));
    }

    setState((prev) => ({ ...prev, data, isLoading: false }));
  };

  const refetchFn = tryCatcher(async (...args) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    const response = await fetchData(...args);
    if (!response || !response.isSuccess) {
      return setState((prev) => ({ ...prev, isLoading: false }));
    }

    if (settings.onSuccess) {
      settings.onSuccess(response.data);
    }

    setState((prev) => ({
      ...prev,
      data: response?.data || prev.data,
      isLoading: false,
    }));
  });

  const [state, setState, stateRef] = useStateRef({
    data: settings.defaultState,
    isError: false,
    isSuccess: false,
    isLoading: settings.startOnMount,
    hasFetched: false,
    error: null,
    mutate: mutateFn,
    refetch: refetchFn,
  });

  const [changeTimes, setChangeTimes] = useState(0);

  const fetchData = async (...args) => {
    if (key && cache.hasOwnProperty(key)) {
      if (settings.triggerLoadingOnMutate) {
        setState((prev) => ({ ...prev, isLoading: true }));
        await sleep(1000);
      }

      if (settings.onSuccess) {
        settings.onSuccess(cache[key]);
      }

      if (settings.onDataChange) {
        const _changeTimes = changeTimes + 1;
        setChangeTimes(_changeTimes);
        settings.onDataChange(cache[key], _changeTimes);
      }

      return setState({
        data: cache[key],
        isError: false,
        isSuccess: true,
        isLoading: false,
        hasFetched: true,
        error: null,
        mutate: mutateFn,
        refetch: refetchFn,
      });
    }
    // // to hit cache and not make a request.
    // if (!defaultOptions.shouldRefetchOnKeyChange && state.isSuccess) {
    //   return;
    // }
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await fetcher(...args);
      if (response.ok === false) {
        if (response instanceof FetchError) {
          throw response.response;
        } else {
          throw response;
        }
      }

      let data;
      if (response instanceof FetchSuccess) {
        data = response.data;
      } else {
        data = await response.json();
      }

      if (settings.shapeData) {
        data = settings.shapeData(data);
      }

      if (key && settings.hasCache) {
        setCache((prev) => ({ ...prev, [key]: data }));
      }

      setState((prev) => ({
        ...prev,
        data,
        isSuccess: true,
        isError: false,
        mutate: mutateFn,
        hasFetched: true,
        refetch: refetchFn,
      }));

      if (onSuccess) {
        onSuccess(data);
      }

      if (settings.onDataChange) {
        const _changeTimes = changeTimes + 1;
        setChangeTimes(_changeTimes);
        settings.onDataChange(data, _changeTimes);
      }

      let newState;
      setState((prev) => {
        newState = { ...prev, isLoading: false };
        return newState;
      });

      return newState;
    } catch (error) {
      if (onError) {
        onError(error);
      }

      if (settings.retries > 0) {
        await sleep(settings.retryDelay);
        settings.retries--;
        fetchData();
      } else {
        setState((prev) => {
          const newState = {
            ...prev,
            isError: true,
            error,
          };

          if (settings.resetStateOnError) {
            newState.data = settings.defaultState;
          }

          return newState;
        });
      }
    } finally {
      setState((prev) => {
        return { ...prev, isLoading: false };
      });

      settings.startAfter = 0;
    }
  };

  useComponentDidMount(() => {
    let canContinue = true;
    if (
      settings.refetchOnConditions &&
      Array.isArray(settings.refetchOnConditions)
    ) {
      settings.refetchOnConditions.forEach((condition) => {
        if (typeof condition === "function") {
          canContinue = condition();
        } else {
          canContinue = !!condition;
        }
      });
    }

    if (!canContinue) return;

    if (settings.shouldResetData()) {
      return setState((prev) => ({
        ...prev,
        data: settings.defaultState,
      }));
    }

    if (!settings.enabled) return;
    fetchData();
  }, [key, ...settings.refetchOnConditions]);

  useEffect(() => {
    if (!settings.startOnMount || !settings.enabled) return;
    const t = setTimeout(fetchData, startAfter || 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.enabled]);

  return { ...state, hashKeys: Object.keys(cache), cache };
};

export const useModifiedFetcher = (fetcher, opts) => {
  let defaultOptions = {
    hasCache: true,
    onSuccess: () => { },
  };

  if (opts) {
    defaultOptions = { ...defaultOptions, ...opts };
  }

  const [cache, setCache] = useState({});

  const [state, setState, stateRef] = useStateRef({
    key: null,
    data: null,
    isError: false,
    isSuccess: false,
    isLoading: false,
    error: null,
  });
  // I am using key to separate the fetches and the loading state
  const modifiedFetcher = async (key, ...args) => {
    let _args = args || [];
    if (args.length === 0) {
      _args = [key]; // to be spread-ed...
    }

    if (cache.hasOwnProperty(key) && defaultOptions.hasCache) {
      setState({
        data: cache[key],
        isError: false,
        isSuccess: true,
        isLoading: false,
        error: null,
      });

      return stateRef.current;
    }

    setState((prev) => ({ ...prev, isLoading: true, key }));
    try {
      const response = await fetcher(..._args);
      if (response.ok === false) {
        if (response instanceof FetchError) {
          throw response.response;
        } else {
          throw response;
        }
      }

      let data;
      if (response instanceof FetchSuccess) {
        data = response.data;
      } else {
        data = await response.json();
      }

      if (defaultOptions.hasCache) {
        setCache((prev) => ({ ...prev, [key]: data }));
      }

      setState((prev) => ({
        ...prev,
        data,
        isSuccess: true,
      }));

      if (defaultOptions.onSuccess) {
        defaultOptions.onSuccess(data);
      }

      return stateRef.current;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isError: true,
        error,
      }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return [state, modifiedFetcher];
};

export default useFetcher;
